import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Route, Ship, MapPin, Loader2, RefreshCw, Plus, Fuel, Trash2, AlertTriangle } from 'lucide-react';
import { useTimezone } from '@/contexts/TimezoneContext';
import DateTimePicker24h from '@/components/ui/datetime-picker';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

import { tripsApi } from '@/services/tripsApi';
import operationsApi from '@/services/operationsApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

// Modos de carga (PRD §5): escoteira (~950 t) | comboiada (~1.900 t) — seleciona o
// coef. de consumo da Ida (usado em F2). Origem fixa nesta fase: Guaíra.
const MODOS_CARGA = [
  { value: 'escoteira', label: 'Escoteira (~950 t)' },
  { value: 'comboiada', label: 'Comboiada (~1.900 t)' },
];
const DEFAULT_ORIGIN = 'Guaíra';

// Selo de status — reusado na tabela do console. INV-UI-TRAIL-001: só display.
const STATUS_BADGE = {
  planejada: { label: 'Planejada', cls: 'bg-sky-600 hover:bg-sky-600 text-white border-transparent' },
  em_andamento: { label: 'Em andamento', cls: 'bg-amber-500 hover:bg-amber-500 text-black border-transparent' },
  fechamento_proposto: { label: 'Fechamento proposto', cls: 'bg-violet-600 hover:bg-violet-600 text-white border-transparent' },
  fechada: { label: 'Fechada', cls: 'bg-emerald-600 hover:bg-emerald-600 text-white border-transparent' },
  cancelada: { label: 'Cancelada', cls: 'bg-muted text-muted-foreground border-transparent line-through' },
};

const fmtDestino = (trip, routesById) => {
  const stop = trip?.planned?.stops?.[0] || {};
  if (stop.porto) return stop.porto;
  const r = routesById[stop.route_id];
  return r?.destino || stop.route_id || '—';
};

// Formata litros (milhar com ponto, pt-BR). null/undefined → '—'. NÃO calcula nada —
// recebe o número já pronto do backend (/queue). INV-UI-TRAIL-001.
const fmtLitros = (v) =>
  v == null ? '—' : `${Math.round(v).toLocaleString('pt-BR')} L`;

// Retorna hoje 06:00 no formato YYYY-MM-DDTHH:mm (para o DateTimePicker24h).
const defaultStartAt = () => {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T06:00`;
};

function TripsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [equipments, setEquipments] = useState([]);
  // filterEquipmentId: '' = "Todas as embarcações"; truthy = equipamento específico (D-03).
  const [filterEquipmentId, setFilterEquipmentId] = useState('');
  // ⌘K deep-link (D-07): /operations/trips?action=new. The "Nova viagem planejada" form only
  // renders once an equipment is selected (D-03) — this hint gives the action a real landing
  // instead of a dead navigation, without altering the existing form logic.
  const [newTripHint] = useState(() => searchParams.get('action') === 'new');
  // Filtros adicionais do console (Fase 5).
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPeriodo, setFilterPeriodo] = useState('');

  const [routes, setRoutes] = useState([]);
  const [trips, setTrips] = useState([]);
  const [queue, setQueue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const { formatDateTime } = useTimezone();

  // Formulário "Nova viagem planejada" (visível só com equipamento selecionado — D-03).
  const [routeId, setRouteId] = useState('');
  const [modoCarga, setModoCarga] = useState('comboiada');
  // Produto default AREIA (decisão do dono 2026-07-02) — editável.
  const [produto, setProduto] = useState('AREIA');
  // Volume previsto (t): pré-preenchido pela capacidade do modo, editável (CC-2026-07-02-005).
  const [volumePrevisto, setVolumePrevisto] = useState('');
  const [startAt, setStartAt] = useState(defaultStartAt());
  const [submitting, setSubmitting] = useState(false);

  // Modos de carga da EMBARCAÇÃO selecionada (equipments.cargo_modes); sem cadastro,
  // caem os legados escoteira/comboiada (operação comboiada do DRA-0006).
  const selectedEquipment = React.useMemo(
    () => (equipments || []).find((e) => e.id === filterEquipmentId) || null,
    [equipments, filterEquipmentId],
  );
  const modosDisponiveis = React.useMemo(() => {
    const modes = selectedEquipment?.cargo_modes;
    if (Array.isArray(modes) && modes.length > 0) {
      return modes
        .filter((m) => m && m.id)
        .map((m) => ({
          value: m.id,
          label: m.capacidade_t
            ? `${m.label || m.id} (~${Number(m.capacidade_t).toLocaleString('pt-BR')} t)`
            : (m.label || m.id),
          capacidade_t: m.capacidade_t ?? null,
        }));
    }
    return MODOS_CARGA.map((m) => ({ ...m, capacidade_t: null }));
  }, [selectedEquipment]);

  // Troca de embarcação/modos: garante modo válido + pré-preenche o volume do modo.
  React.useEffect(() => {
    const cur = modosDisponiveis.find((m) => m.value === modoCarga);
    const eff = cur || modosDisponiveis[0];
    if (!cur && eff) setModoCarga(eff.value);
    setVolumePrevisto(eff?.capacidade_t != null ? String(eff.capacidade_t) : '');
  }, [modosDisponiveis, modoCarga]);

  // Estado para o Dialog de cancelamento (único, fora do map).
  const [cancelTripTarget, setCancelTripTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');

  const routesById = React.useMemo(
    () => Object.fromEntries((routes || []).map((r) => [r.id, r])),
    [routes],
  );

  // Índice trip_id → item da fila (/queue). Os números (saldo_volta_l/refuel_here)
  // já vêm prontos do backend — aqui só indexamos para exibir por linha. INV-UI-TRAIL-001.
  // Populado SOMENTE quando filterEquipmentId está setado (Pitfall 4 — /queue é por 1 equipamento).
  const queueByTripId = React.useMemo(
    () => Object.fromEntries((queue?.trips || []).map((t) => [t.trip_id, t])),
    [queue],
  );
  const firstRefuelTripId = queue?.first_refuel_trip_id ?? null;
  // saldo indisponível (no_baseline / influx_unavailable): a fila ainda lista consumos.
  const saldoIndisponivel = Boolean(queue?.saldo_error);

  const loadEquipments = useCallback(async () => {
    try {
      const res = await operationsApi.getEquipments();
      const data = res.data || [];
      setEquipments(data);
      // Fase 5: NÃO auto-selecionar o primeiro equipamento — filterEquipmentId começa em '' (todas).
    } catch (e) {
      console.error('[Trips] erro ao carregar embarcações:', e);
      toast.error('Erro ao carregar a lista de embarcações.');
    }
  }, []);

  const loadRoutes = useCallback(async () => {
    try {
      const res = await tripsApi.getRoutes();
      // GET /api/lab/routes retorna { routes: [...] } (mesmo padrão de RoutesPage/GeofencesPage)
      setRoutes(res.data?.routes || res.data || []);
    } catch (e) {
      console.error('[Trips] erro ao carregar rotas/destinos:', e);
      toast.error('Erro ao carregar a lista de destinos (rotas).');
    }
  }, []);

  // Console multi-equipamento: carrega viagens via /console com escopo RBAC do backend.
  // Queue (/queue) é carregado em paralelo SOMENTE quando filterEquipmentId está setado
  // (endpoint de fila é por equipamento único). INV-UI-TRAIL-001: frontend só exibe.
  const loadConsoleTrips = useCallback(async () => {
    setLoadError(false);
    setLoading(true);
    setTrips([]);
    setQueue(null);

    // Monta params a partir dos filtros ativos.
    const params = {};
    if (filterEquipmentId) params.equipment_id = filterEquipmentId;
    if (filterStatus) params.status = filterStatus;
    // Período → traduz em start_after ISO (filtro de navegação, não recomputa telemetria).
    if (filterPeriodo === 'hoje') {
      const now = new Date();
      params.start_after = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
    } else if (filterPeriodo === 'semana') {
      const now = new Date();
      params.start_after = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (filterPeriodo === 'mes') {
      const now = new Date();
      params.start_after = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    }

    try {
      // Viagens + fila em paralelo. Queue só quando um equipamento está selecionado.
      const [tripsRes, queueRes] = await Promise.all([
        tripsApi.listConsole(params),
        filterEquipmentId
          ? tripsApi.getQueue(filterEquipmentId).catch((e) => {
              console.error('[Trips] erro ao carregar fila (/queue):', e);
              return null;
            })
          : Promise.resolve(null),
      ]);
      setTrips(tripsRes.data || []);
      setQueue(queueRes?.data || null);
    } catch (e) {
      console.error('[Trips] erro ao carregar viagens:', e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [filterEquipmentId, filterStatus, filterPeriodo]);

  useEffect(() => {
    loadEquipments();
    loadRoutes();
  }, [loadEquipments, loadRoutes]);

  useEffect(() => {
    loadConsoleTrips();
  }, [loadConsoleTrips]);

  const handleCreate = async () => {
    if (!filterEquipmentId) {
      toast.error('Selecione uma embarcação.');
      return;
    }
    if (!routeId) {
      toast.error('Selecione o destino da viagem.');
      return;
    }
    setSubmitting(true);
    try {
      const volumeNum = volumePrevisto !== '' ? Number(volumePrevisto) : null;
      await tripsApi.createTrip({
        equipment_id: filterEquipmentId,
        planned: {
          origin: DEFAULT_ORIGIN,
          stops: [{ route_id: routeId, ordem: 1, produto: produto || null }],
          modo_carga: modoCarga,
          start_at: startAt ? `${startAt}:00-03:00` : null,
          volume_previsto_t: Number.isFinite(volumeNum) && volumeNum > 0 ? volumeNum : null,
        },
      });
      toast.success('Viagem planejada criada.');
      // Limpa o form e recarrega o console (produto volta ao default AREIA).
      setRouteId('');
      setProduto('AREIA');
      setModoCarga(modosDisponiveis[0]?.value || 'comboiada');
      setStartAt(defaultStartAt());
      await loadConsoleTrips();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao criar a viagem.';
      console.error('[Trips] erro ao criar viagem:', e);
      toast.error(typeof msg === 'string' ? msg : 'Erro ao criar a viagem.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page-container space-y-6" data-testid="trips-page">
      {/* Header (UI-SPEC "Header") */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="pl-12 md:pl-0">
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Route className="w-6 h-6 text-primary" />
            Viagens
          </h1>
          <p className="text-sm text-muted-foreground">
            Central de operações — acompanhe as viagens dos equipamentos atribuídos
          </p>
        </div>
        <Button
          variant="outline"
          onClick={loadConsoleTrips}
          disabled={loading}
          className="gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Atualizar lista
        </Button>
      </div>

      {/* ⌘K "Nova viagem" landing (D-07): orienta o dono a selecionar um equipamento antes de
          o formulário "Nova viagem planejada" (gated por filterEquipmentId, D-03) aparecer. */}
      {newTripHint && !filterEquipmentId && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm">Selecione um equipamento acima para criar a viagem.</p>
          </CardContent>
        </Card>
      )}

      {/* Tira de filtros (UI-SPEC "Filters Strip") */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Filtro 1 — Equipamento */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Ship className="w-4 h-4 text-muted-foreground" />
                Embarcação
              </Label>
              <Select value={filterEquipmentId || 'all'} onValueChange={(v) => setFilterEquipmentId(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="trips-equipment">
                  <SelectValue placeholder="Todas as embarcações" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as embarcações</SelectItem>
                  {equipments.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.tag ? `(${e.tag})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtro 2 — Status */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="planejada">Planejada</SelectItem>
                  <SelectItem value="em_andamento">Em andamento</SelectItem>
                  <SelectItem value="fechamento_proposto">Fechamento proposto</SelectItem>
                  <SelectItem value="fechada">Fechada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Filtro 3 — Período */}
            <div className="space-y-2">
              <Label>Período</Label>
              <Select value={filterPeriodo || 'all'} onValueChange={(v) => setFilterPeriodo(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Qualquer período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Qualquer período</SelectItem>
                  <SelectItem value="hoje">Hoje</SelectItem>
                  <SelectItem value="semana">Esta semana</SelectItem>
                  <SelectItem value="mes">Este mês</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Nova viagem planejada — renderiza SOMENTE quando equipamento específico selecionado (D-03).
          "Ver tudo" vs "agir sobre um": o form é ação sobre um equipamento. */}
      {filterEquipmentId && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4 text-primary" />
              Nova viagem planejada
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  Destino
                </Label>
                <Select value={routeId} onValueChange={setRouteId}>
                  <SelectTrigger data-testid="trips-destino">
                    <SelectValue placeholder="Selecione o destino" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.destino} {r.comprimento_km != null ? `(${r.comprimento_km} km)` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Modo de carga</Label>
                <Select value={modoCarga} onValueChange={setModoCarga}>
                  <SelectTrigger data-testid="trips-modo-carga">
                    <SelectValue placeholder="Modo de carga" />
                  </SelectTrigger>
                  <SelectContent>
                    {modosDisponiveis.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Volume previsto (t)</Label>
                <Input
                  type="number"
                  min="0"
                  value={volumePrevisto}
                  onChange={(ev) => setVolumePrevisto(ev.target.value)}
                  placeholder="toneladas"
                  data-testid="trips-volume-previsto"
                />
              </div>

              <div className="space-y-2">
                <Label>Produto</Label>
                <Input
                  value={produto}
                  onChange={(ev) => setProduto(ev.target.value)}
                  placeholder="AREIA"
                  data-testid="trips-produto"
                />
              </div>

              <div className="space-y-2">
                <Label>Data/hora prevista de início</Label>
                <DateTimePicker24h
                  value={startAt}
                  onChange={setStartAt}
                  ariaLabel="Data/hora prevista de início"
                  data-testid="trips-start-at"
                />
              </div>

              <Button
                onClick={handleCreate}
                disabled={submitting || !filterEquipmentId || !routeId}
                className="gap-2"
                data-testid="trips-create-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Criar viagem
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Origem fixa: {DEFAULT_ORIGIN}. 1 destino por viagem (multi-stop diferido). Nesta fase só
              é possível programar e listar — acompanhar/encerrar virá em fases futuras.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Tabela do console (UI-SPEC "Table", 9 colunas) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-3">
            <Route className="w-4 h-4 text-primary" />
            Viagens
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Banner de abastecimento — per-equipment, exibido SOMENTE quando equipamento filtrado.
              A badge "Abastecer antes" na coluna Saldo permanece visível em todas as visões. */}
          {filterEquipmentId && queue && (
            firstRefuelTripId ? (
              <div
                className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm"
                data-testid="trips-refuel-indicator"
              >
                <Fuel className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <span className="flex-1">
                  Abastecer antes de iniciar a viagem{' '}
                  <span className="font-mono font-semibold">{firstRefuelTripId}</span>
                  {' '}— o saldo projetado no retorno cai abaixo da reserva ({fmtLitros(queue.reserve_l)}).
                </span>
                {/* Deep-link (backlog 3.1): do problema à ação em 1 clique */}
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-amber-500/60"
                  data-testid="trips-refuel-deeplink"
                  onClick={() => navigate(`/operations/fuel-control?equipment=${encodeURIComponent(filterEquipmentId)}&action=refuel`)}
                >
                  Lançar abastecimento
                </Button>
              </div>
            ) : saldoIndisponivel ? (
              <div
                className="mb-4 rounded-md border border-muted bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
                data-testid="trips-refuel-indicator"
              >
                Saldo do tanque indisponível — a fila lista os consumos projetados, mas não há
                projeção de saldo na volta nem indicação de abastecimento.
              </div>
            ) : (
              <div
                className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700"
                data-testid="trips-refuel-indicator"
              >
                Sem necessidade de abastecimento na janela — reserva de referência {fmtLitros(queue.reserve_l)}.
              </div>
            )
          )}

          {/* Estado: despachante sem equipamento atribuído — fora da tabela (UI-SPEC) */}
          {!loading && !loadError && equipments.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              <span className="block font-semibold">Nenhum equipamento atribuído.</span>
              <span className="block text-sm mt-1">
                Solicite ao administrador acesso às embarcações para visualizar as viagens.
              </span>
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Equipamento</TableHead>
                  <TableHead>VGM</TableHead>
                  <TableHead>Destino</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Início</TableHead>
                  <TableHead>Saldo projetado no retorno</TableHead>
                  <TableHead>Retorno previsto</TableHead>
                  <TableHead>Consumo previsto</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Estado de loading */}
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                      Carregando viagens…
                    </TableCell>
                  </TableRow>
                ) : loadError ? (
                  /* Estado de erro */
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-12">
                      <AlertTriangle className="w-5 h-5 text-destructive inline mr-2" />
                      <span className="text-sm text-muted-foreground">Erro ao carregar as viagens.</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-3 gap-2"
                        onClick={loadConsoleTrips}
                      >
                        <RefreshCw className="w-4 h-4" />
                        Atualizar lista
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : trips.length === 0 ? (
                  /* Estado vazio — nenhuma viagem nos filtros */
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-12">
                      <p className="font-semibold">Nenhuma viagem encontrada</p>
                      <p className="text-sm mt-1">Selecione outro filtro ou aguarde o registro de novas viagens.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  /* Linhas das viagens — clicáveis, navegam para /operations/trips/:id */
                  trips.map((trip) => {
                    const badge = STATUS_BADGE[trip.status] || {
                      label: trip.status || '—',
                      cls: 'bg-muted text-muted-foreground border-transparent',
                    };
                    const qItem = queueByTripId[trip.id];
                    return (
                      <TableRow
                        key={trip.id}
                        className="hover:bg-muted/40 cursor-pointer"
                        onClick={(e) => {
                          if (e.defaultPrevented) return;
                          navigate('/operations/trips/' + trip.id);
                        }}
                      >
                        {/* Col 1: Equipamento */}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{trip.equipment_name || '—'}</span>
                            {trip.no_telemetry && (
                              <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                                Sem telemetria
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground">{trip.equipment_tag || ''}</div>
                        </TableCell>
                        {/* Col 2: VGM */}
                        <TableCell className="font-mono text-xs">{trip.id}</TableCell>
                        {/* Col 3: Destino */}
                        <TableCell>{fmtDestino(trip, routesById)}</TableCell>
                        {/* Col 4: Status — em_andamento = LIVE (bolinha vermelha pulsante,
                            decisão do dono 2026-07-02: o despachante VÊ que o sistema está
                            rastreando a viagem em execução). */}
                        <TableCell>
                          <span className="flex items-center gap-2">
                            {trip.status === 'em_andamento' && (
                              <span className="relative flex h-2.5 w-2.5" title="Ao vivo — viagem em execução">
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-600" />
                              </span>
                            )}
                            <Badge className={badge.cls}>
                              {trip.status === 'em_andamento' ? 'AO VIVO' : badge.label}
                            </Badge>
                          </span>
                        </TableCell>
                        {/* Col 5: Início — formatDateTime, sem recompute. INV-UI-TRAIL-001. */}
                        <TableCell className="text-sm">
                          {trip.planned?.start_at
                            ? formatDateTime(trip.planned.start_at, { omitSeconds: true })
                            : '—'}
                        </TableCell>
                        {/* Col 6: Saldo projetado no retorno — backend já computou. */}
                        <TableCell>
                          {qItem == null ? (
                            <span className="text-muted-foreground">—</span>
                          ) : qItem.saldo_volta_l == null ? (
                            <span className="text-muted-foreground">saldo indisponível</span>
                          ) : (
                            <span className="flex items-center gap-2">
                              <span className={qItem.refuel_here ? 'font-semibold text-amber-600' : ''}>
                                {fmtLitros(qItem.saldo_volta_l)}
                              </span>
                              {qItem.refuel_here && (
                                <Badge className="bg-amber-500 hover:bg-amber-500 text-black border-transparent">
                                  Abastecer antes
                                </Badge>
                              )}
                            </span>
                          )}
                        </TableCell>
                        {/* Col 7: Retorno previsto — backend. INV-UI-TRAIL-001. */}
                        <TableCell
                          className="text-sm"
                          title={
                            qItem?.duracao_base
                              ? `mediana de ${qItem.duracao_base.n_viagens} viagens, janela ${qItem.duracao_base.janela_dias}d`
                              : undefined
                          }
                        >
                          {qItem?.retorno_previsto
                            ? formatDateTime(qItem.retorno_previsto, { omitSeconds: true })
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        {/* Col 8: Consumo previsto — backend. INV-UI-TRAIL-001. */}
                        <TableCell
                          title={
                            qItem?.consumo_base
                              ? `mediana das últimas ${qItem.consumo_base.n_viagens} viagens`
                              : undefined
                          }
                        >
                          <span className="flex items-center gap-2">
                            <span>{fmtLitros(qItem?.consumo_previsto_l)}</span>
                            {qItem?.estimativa_sem_historico && (
                              <Badge
                                className="bg-muted text-muted-foreground border-transparent"
                                title="estimativa sem histórico"
                              >
                                est. sem histórico
                              </Badge>
                            )}
                          </span>
                        </TableCell>
                        {/* Col 9: Ações — stopPropagation para não disparar navegação da linha */}
                        <TableCell>
                          {trip.status === 'planejada' ? (
                            <AlertDialog>
                              {/* Vincular ao ciclo ATIVO (fila estava vazia quando abriu —
                                  decisão do dono 2026-07-02). Server-authoritative: 409
                                  vira toast se não há ciclo/já vinculado. */}
                              <Button
                                variant="outline"
                                size="sm"
                                className="mr-2 gap-1"
                                data-testid={`trips-link-cycle-${trip.id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  try {
                                    await tripsApi.linkCycle(trip.id);
                                    toast.success('Viagem vinculada ao ciclo ativo (AO VIVO).');
                                    await loadConsoleTrips();
                                  } catch (err) {
                                    const msg = err?.response?.data?.detail;
                                    toast.error(typeof msg === 'string' ? msg : 'Erro ao vincular.');
                                  }
                                }}
                              >
                                Vincular
                              </Button>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Excluir
                                </Button>
                              </AlertDialogTrigger>
                              {/* stopPropagation: cliques no dialog (portal) não devem
                                  borbulhar pela árvore React até o onClick da linha. */}
                              <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Excluir viagem?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    VGM <span className="font-mono">{trip.id}</span> — Destino:{' '}
                                    {fmtDestino(trip, routesById)}. Esta ação é irreversível.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                    onClick={async () => {
                                      try {
                                        await tripsApi.deleteTrip(trip.id);
                                        toast.success('Viagem excluída.');
                                        await loadConsoleTrips();
                                      } catch (err) {
                                        const msg =
                                          err?.response?.data?.detail || 'Erro ao excluir a viagem.';
                                        toast.error(
                                          typeof msg === 'string' ? msg : 'Erro ao excluir a viagem.',
                                        );
                                      }
                                    }}
                                  >
                                    Excluir
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          ) : trip.status === 'em_andamento' || trip.status === 'fechada' ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setCancelTripTarget(trip);
                              }}
                            >
                              Cancelar
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}

          {/* Reserva de referência sob a tabela — só quando equipamento filtrado. */}
          {filterEquipmentId && queue && (
            <p className="mt-3 text-xs text-muted-foreground">
              Reserva de referência (piso): {fmtLitros(queue.reserve_l)}. O saldo projetado na volta
              é encadeado pela fila e computado no backend.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dialog de cancelamento — único, fora do map para evitar múltiplos portais. */}
      <Dialog
        open={!!cancelTripTarget}
        onOpenChange={(o) => {
          if (!o) {
            setCancelTripTarget(null);
            setCancelReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar viagem</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo (obrigatório)</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Informe o motivo do cancelamento"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCancelTripTarget(null);
                setCancelReason('');
              }}
            >
              Fechar
            </Button>
            <Button
              variant="destructive"
              disabled={!cancelReason.trim()}
              onClick={async () => {
                try {
                  await tripsApi.cancelTrip(cancelTripTarget.id, cancelReason);
                  toast.success('Viagem cancelada.');
                  setCancelTripTarget(null);
                  setCancelReason('');
                  await loadConsoleTrips();
                } catch (e) {
                  const msg = e?.response?.data?.detail || 'Erro ao cancelar a viagem.';
                  toast.error(typeof msg === 'string' ? msg : 'Erro ao cancelar a viagem.');
                }
              }}
            >
              Confirmar cancelamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TripsPage;
